import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import { comnEnvs, comnUtils } from "@/comn/utils";
import { Page, Group, Layout, Button, Grid, ExcelUpload, FormControl, Table } from "@/comn/components";
import {
    useForm,
    useToast,
    useFetch,
    useModal,
    useAuth,
    useStore,
    useGrid,
    usePopup,
    useResource,
    usePage,
} from "@/comn/hooks";
import { APIS, SF_JNT_INSC_ASIN_INFO, SF_JNT_INSC_ASIN_NEW, URLS } from "./services/OsciLpcoAsinService";
import { api } from "@/comn";
import { MASK } from "@/tz/go/egaz/osci/lpco/comn/LpcoConstants";
import { UsrLstbyRole } from "../../comn/UsrLstbyRole";
/*
 * @ 화면 컴포넌트 주석
 * @ 메타 시스템을 참고 하여 표준 단어와 용어를 사용하여 작성
 * @ !== ... ==! 치환 부호는 개발 완료 후 한글 주석을 인지하여 제거하기 위한 표시
 * @ 기본 여러줄의 주석 형태를 사용
 * @ 포맷
 * @ [화면명 영문]
 * @ [지환부호시작] [화면명 한글] [치환부호종료]
 *
 * 화면명 영문
 * !== 화면명 한글 ==!
 */

/*
 * Modify LPCO Assignment
 * !== LPCO Assignment 수정 ==!
 */
export const OSCI0201028S = () => {
    useResource({
        defaultSchema: [{ area: "comnCd", comnCd: "OSC0116" }],
    });
    const { pgeUid, menuId } = usePage({
        auth: ["auth_osci0002", "auth_osci0004"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const auth = useAuth(); // Login , User Information Hook !== 로그인, 유저정보 Hook ==!
    const { openPopup } = usePopup();
    const [URLSearchParams] = useSearchParams();

    const asnmtOfcrTpCd = URLSearchParams.get("asnmtOfcrTpCd");

    const { tansadNo } = useParams();
    const refOrginData = useRef({});

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Register LPCO Component !== LPCO Component 등록 ==!
        jntInscAsinInfo: useForm({
            defaultSchema: SF_JNT_INSC_ASIN_INFO,
            defaultValues: {},
        }),
        jntInscAsinNewInfo: useForm({
            defaultSchema: SF_JNT_INSC_ASIN_NEW,
            defaultValues: {},
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {};

    const render = {};

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        getJntInscRsptyInfo: useFetch({
            api: (tansadNo: any) => APIS.getJntInscRsptyInfo(tansadNo),
            onSuccess: (data) => {
                const info = data.rsptyInfo.content || {};
                form.jntInscAsinInfo.setValues({ ...info });
                form.jntInscAsinNewInfo.setValues({ asinOfcrId: info.asinOfcrId });

                refOrginData.current = { ...info, excuUserId: info.asinOfcrId };
            },
            onError: (error) => {
                let message = error.response?.data?.errorMessage || "msg.osc.00035";

                modal.openModal({
                    content: message,
                    onClose: () => {
                        navigate(`${URLS.osci0201026q}`);
                    },
                });
            },
        }),
        createChangeRspty: useFetch({
            api: (data: any) => APIS.createChangeInscRspty({ ...data }),
            onSuccess: (data) => {
                modal.openModal({
                    content: "msg.osc.00006" /**Processed successfully. */,
                    onConfirm: () => {
                        navigate(URLS.osci0201026q);
                    },
                    onCancel: () => {
                        navigate(URLS.osci0201026q);
                    },
                });
            },
            onError: (error) => {
                modal.openModal({
                    content: "msg.osc.00015",
                });
            },
        }),
    };
    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        onRsptySelected: (data: any) => {
            form.jntInscAsinNewInfo.setValue("newAsinOfcrId", data.code);
            form.jntInscAsinNewInfo.setValue("newOrgCd", data.orgCd);

            console.log("form.appAsinNewInfo ", form.jntInscAsinNewInfo.getValues());
        },
        createChangeRspty: form.jntInscAsinNewInfo.handleSubmit(async () => {
            form.jntInscAsinNewInfo.setValue("asnmtOfcrTpCd", asnmtOfcrTpCd);
            form.jntInscAsinNewInfo.setValue("tansadNo", tansadNo);
            const data = { ...form.jntInscAsinNewInfo.getValues() };

            modal.openModal({
                content: "msg.osc.00090" /**Do you want to save ? */,
                onConfirm: () => {
                    fetch.createChangeRspty.fetch(data);
                },
            });
        }),
    };

    useEffect(() => {
        fetch.getJntInscRsptyInfo.fetch(tansadNo);
    }, []);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Title title={"T_ASIN_INFO"} titleSize={2}></Group.Title>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.jntInscAsinInfo.schema.orgNm}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control
                                {...form.jntInscAsinInfo.schema.tansadNo}
                                mask={MASK.TANSAD_NO}
                            ></Group.Control>
                            <Group.Label {...form.jntInscAsinInfo.schema.reffNo}></Group.Label>
                            <Group.Field size={4}>
                                <Group.Control {...form.jntInscAsinInfo.schema.reffTin} />
                                <Group.Any>-</Group.Any>
                                <Group.Control {...form.jntInscAsinInfo.schema.reffYy} />
                                <Group.Any>-</Group.Any>
                                <Group.Control {...form.jntInscAsinInfo.schema.reffSrno} />
                            </Group.Field>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.jntInscAsinInfo.schema.godsLoctWrhsCd}></Group.Control>
                            <Group.Control {...form.jntInscAsinInfo.schema.cntrLstCn}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.jntInscAsinInfo.schema.asnmtOfcrTpCd}></Group.Control>
                            <Group.Control {...form.jntInscAsinInfo.schema.asinOfcrId}></Group.Control>
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>
            <Group>
                <Group.Body>
                    <Group.Title title={"T_ASIN_INFO"} titleSize={2}></Group.Title>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.jntInscAsinNewInfo.schema.asinOfcrId}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control
                                {...form.jntInscAsinNewInfo.schema.newAsinOfcrId}
                                controlSize={5}
                                rightButton={{
                                    icon: "search",
                                    onClick: () => {
                                        modal.openModal({
                                            content: (
                                                <UsrLstbyRole
                                                    params={{ ...refOrginData.current }}
                                                    onSelected={handler.onRsptySelected}
                                                />
                                            ),
                                            size: "md",
                                            draggable: true,
                                        });
                                    },
                                }}
                            ></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control
                                {...form.jntInscAsinNewInfo.schema.asinMdfyRsn}
                                controlSize={10}
                            ></Group.Control>
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>
            <Layout direction="row">
                <Layout.Left>
                    <Button
                        role="list"
                        onClick={() => {
                            navigate(URLS.osci0201026q);
                        }}
                    ></Button>
                    <Button
                        role="reset"
                        onClick={() => {
                            form.jntInscAsinNewInfo.setValue("newAsinOfcrId", "");
                            form.jntInscAsinNewInfo.setValue("asinMdfyRsn", "");
                        }}
                    ></Button>
                </Layout.Left>
                <Layout.Right>
                    <Button role="save" onClick={handler.createChangeRspty}></Button>
                    <Button role="cancel" onClick={() => navigate(`${URLS.osci0201026q}`)}></Button>
                </Layout.Right>
            </Layout>
        </Page>
    );
};
